/**
 * F-148 §4.6 (AC3, AC7): server-side variant search beyond 200 rows with keyset "Load more".
 * Prop-driven: the route owns the infinite query and passes pages down. Exact barcode matches are
 * shown first. Selecting a variant is an explicit merchant choice (`matchMethod = 'manual'`).
 */
import { useEffect, useId, useRef } from 'react';
import { Loader2, Search } from 'lucide-react';
import { useTranslation } from '@dukanti/shared';
import type { ReceivingVariantSnapshot } from '@dukanti/shared/admin';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

export interface VariantPickerDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly query: string;
  readonly onQueryChange: (query: string) => void;
  readonly items: readonly ReceivingVariantSnapshot[];
  readonly exactBarcode: ReceivingVariantSnapshot | null;
  readonly isLoading?: boolean;
  readonly isError?: boolean;
  readonly hasMore?: boolean;
  readonly isLoadingMore?: boolean;
  readonly onLoadMore: () => void;
  readonly onSelect: (variant: ReceivingVariantSnapshot) => void;
  readonly imageUrlFor?: (path: string) => string | null;
}

function VariantOption({
  variant,
  onSelect,
  imageUrlFor,
  exact,
}: {
  variant: ReceivingVariantSnapshot;
  onSelect: (variant: ReceivingVariantSnapshot) => void;
  imageUrlFor?: (path: string) => string | null;
  exact?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const src = variant.imagePath && imageUrlFor ? imageUrlFor(variant.imagePath) : null;
  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(variant)}
        disabled={!variant.eligible}
        className={cn(
          'hover:bg-admin-surface-muted focus-visible:ring-ring/50 flex min-h-14 w-full items-center gap-3 rounded-lg px-3 py-2 text-start outline-none focus-visible:ring disabled:cursor-not-allowed disabled:opacity-60',
          exact ? 'bg-admin-lime-light/40' : '',
        )}
        data-testid="variant-option"
        data-variant-id={variant.variantId}
      >
        <div className="bg-admin-surface-muted size-10 shrink-0 overflow-hidden rounded-md">
          {src ? <img src={src} alt="" className="size-full object-cover" loading="lazy" /> : null}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-foreground truncate text-sm font-medium">
            <bdi>{variant.productName}</bdi>
          </p>
          {variant.optionSummary ? (
            <p className="text-muted-foreground truncate text-xs">
              <bdi>{variant.optionSummary}</bdi>
            </p>
          ) : null}
          <p className="text-muted-foreground text-xs">
            {t('admin.barcode.result.stock')}: <bdi dir="ltr">{variant.inventory}</bdi>
            {variant.barcode ? (
              <>
                {' · '}
                <bdi dir="ltr" className="font-mono">
                  {variant.barcode}
                </bdi>
              </>
            ) : null}
          </p>
        </div>
        {!variant.active ? (
          <Badge variant="neutral">{t('admin.receiving.line.inactiveBadge')}</Badge>
        ) : null}
      </button>
    </li>
  );
}

export function VariantPickerDialog({
  open,
  onOpenChange,
  query,
  onQueryChange,
  items,
  exactBarcode,
  isLoading = false,
  isError = false,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  onSelect,
  imageUrlFor,
}: VariantPickerDialogProps): React.JSX.Element {
  const { t, dir } = useTranslation();
  const id = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) {
      const handle = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(handle);
    }
    return undefined;
  }, [open]);

  const listItems = exactBarcode
    ? items.filter((item) => item.variantId !== exactBarcode.variantId)
    : items;
  const empty = !isLoading && !isError && listItems.length === 0 && !exactBarcode;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        dir={dir}
        className="max-h-[min(90dvh,44rem)] w-[calc(100vw-1.5rem)] max-w-lg overflow-hidden rounded-2xl p-0"
        data-testid="variant-picker-dialog"
      >
        <div className="flex max-h-[min(90dvh,44rem)] flex-col">
          <DialogHeader className="p-5 pb-3">
            <DialogTitle>{t('admin.receiving.picker.title')}</DialogTitle>
            <DialogDescription>{t('admin.receiving.picker.search')}</DialogDescription>
          </DialogHeader>
          <div className="px-5">
            <Label htmlFor={`${id}-search`} className="sr-only">
              {t('admin.receiving.picker.search')}
            </Label>
            <div className="relative">
              <Search
                aria-hidden="true"
                className="text-muted-foreground pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2"
              />
              <Input
                ref={inputRef}
                id={`${id}-search`}
                type="search"
                autoComplete="off"
                value={query}
                onChange={(event) => onQueryChange(event.target.value)}
                placeholder={t('common.search')}
                className="min-h-11 ps-9 text-start"
                data-testid="variant-picker-search"
              />
            </div>
          </div>
          <div
            className="min-h-0 flex-1 overflow-y-auto px-3 py-3"
            aria-live="polite"
            aria-busy={isLoading}
          >
            {isLoading ? (
              <p className="text-muted-foreground flex items-center gap-2 px-3 py-6 text-sm">
                <Loader2
                  aria-hidden="true"
                  className="size-4 animate-spin motion-reduce:animate-none"
                />
                {t('common.loading')}
              </p>
            ) : null}
            {isError ? (
              <p role="alert" className="text-destructive px-3 py-6 text-sm">
                {t('common.errors.networkFailed')}
              </p>
            ) : null}
            {empty ? (
              <p
                className="text-muted-foreground px-3 py-6 text-sm"
                data-testid="variant-picker-empty"
              >
                {t('admin.receiving.picker.noResults')}
              </p>
            ) : null}
            {exactBarcode ? (
              <section aria-label={t('admin.receiving.picker.exactBarcode')} className="mb-2">
                <p className="text-muted-foreground px-3 pb-1 text-xs font-semibold uppercase tracking-wide">
                  {t('admin.receiving.picker.exactBarcode')}
                </p>
                <ul>
                  <VariantOption
                    variant={exactBarcode}
                    onSelect={onSelect}
                    imageUrlFor={imageUrlFor}
                    exact
                  />
                </ul>
              </section>
            ) : null}
            {listItems.length > 0 ? (
              <ul
                aria-label={t('admin.receiving.picker.results')}
                data-testid="variant-picker-results"
              >
                {listItems.map((variant) => (
                  <VariantOption
                    key={variant.variantId}
                    variant={variant}
                    onSelect={onSelect}
                    imageUrlFor={imageUrlFor}
                  />
                ))}
              </ul>
            ) : null}
            {hasMore ? (
              <div className="px-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  className="min-h-11 w-full"
                  disabled={isLoadingMore}
                  onClick={onLoadMore}
                  data-testid="variant-picker-load-more"
                >
                  {isLoadingMore ? (
                    <Loader2
                      aria-hidden="true"
                      className="size-4 animate-spin motion-reduce:animate-none"
                    />
                  ) : null}
                  {t('admin.receiving.picker.loadMore')}
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
